import { useEffect, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import type { TOTPCategory } from "@/lib/supabase/client";

interface CategorySelectProps {
  categories: TOTPCategory[];
  selectedCategoryId: string | null;
  onSelect: (category: TOTPCategory | null) => void;
  onCreateCategory: (name: string) => Promise<TOTPCategory | null>;
  placeholder?: string;
  disabled?: boolean;
}

export function CategorySelect({
  categories,
  selectedCategoryId,
  onSelect,
  onCreateCategory,
  placeholder = "Selecione ou crie uma categoria",
  disabled = false,
}: CategorySelectProps) {
  const [query, setQuery] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [pendingCategoryName, setPendingCategoryName] = useState<string | null>(
    null
  );
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  const selectedCategory = useMemo(
    () =>
      categories.find((category) => category.id === selectedCategoryId) ?? null,
    [categories, selectedCategoryId]
  );

  useEffect(() => {
    setQuery(selectedCategory?.name ?? "");
  }, [selectedCategory?.name]);

  const filteredCategories = useMemo(() => {
    if (!query.trim()) {
      return categories;
    }
    const normalizedQuery = query.trim().toLowerCase();
    return categories.filter((category) =>
      category.name.toLowerCase().includes(normalizedQuery)
    );
  }, [categories, query]);

  const shouldShowCreateOption = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) {
      return false;
    }
    return !categories.some(
      (category) => category.name.toLowerCase() === normalizedQuery
    );
  }, [categories, query]);

  const isFiltering = useMemo(() => query.trim().length > 0, [query]);

  const handleSelect = (category: TOTPCategory | null) => {
    onSelect(category);
    setIsDropdownOpen(false);
    setQuery(category?.name ?? "");
  };

  const handleInputChange = (value: string) => {
    setQuery(value);
    setIsDropdownOpen(true);
    if (selectedCategory && value !== selectedCategory.name) {
      onSelect(null);
    }
  };

  const handleRequestCreateCategory = (name: string) => {
    setPendingCategoryName(name);
    setIsConfirmOpen(true);
  };

  const handleCreateCategory = async () => {
    if (!pendingCategoryName) {
      return;
    }
    setIsCreating(true);
    try {
      const created = await onCreateCategory(pendingCategoryName.trim());
      if (created) {
        handleSelect(created);
      }
    } finally {
      setIsCreating(false);
      setIsConfirmOpen(false);
      setPendingCategoryName(null);
    }
  };

  return (
    <>
      <div className="relative">
        <Input
          id="category"
          value={query}
          placeholder={placeholder}
          autoComplete="off"
          onChange={(event) => handleInputChange(event.target.value)}
          onFocus={() => {
            setIsDropdownOpen(true);
            setQuery("");
          }}
          onBlur={() => {
            window.setTimeout(() => {
              setIsDropdownOpen(false);
              setQuery(selectedCategory?.name ?? "");
            }, 150);
          }}
          disabled={disabled}
          className="bg-secondary border-border text-foreground"
        />

        {isDropdownOpen && !disabled && (
          <div className="absolute z-20 mt-1 max-h-48 w-full overflow-y-auto rounded-md border border-border bg-card shadow-lg">
            {!isFiltering && (
              <button
                type="button"
                className={cn(
                  "w-full px-3 py-2 text-left text-sm",
                  selectedCategoryId === null
                    ? "bg-primary text-primary-foreground"
                    : "text-foreground hover:bg-accent"
                )}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => handleSelect(null)}
              >
                Sem categoria
              </button>
            )}
            {filteredCategories.length === 0 && !shouldShowCreateOption && (
              <div className="px-3 py-2 text-sm text-muted-foreground">
                Nenhuma categoria encontrada
              </div>
            )}
            {filteredCategories.map((category) => (
              <button
                key={category.id}
                type="button"
                className={cn(
                  "w-full px-3 py-2 text-left text-sm",
                  category.id === selectedCategoryId
                    ? "bg-primary text-primary-foreground"
                    : "text-foreground hover:bg-accent"
                )}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => handleSelect(category)}
              >
                {category.name}
              </button>
            ))}
            {shouldShowCreateOption && (
              <div className="border-t border-border">
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full justify-start px-3 py-2 text-sm text-foreground hover:bg-accent"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => handleRequestCreateCategory(query.trim())}
                >
                  Criar categoria &quot;{query.trim()}&quot;
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      <AlertDialog
        open={isConfirmOpen}
        onOpenChange={(open) => {
          setIsConfirmOpen(open);
          if (!open) {
            setPendingCategoryName(null);
          }
        }}
      >
        <AlertDialogContent className="bg-card text-foreground border border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Criar nova categoria</AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              Deseja realmente criar a categoria &quot;{pendingCategoryName}
              &quot;?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={isCreating}
              onClick={() => setIsConfirmOpen(false)}
              className="bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={isCreating}
              onClick={handleCreateCategory}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {isCreating ? "Criando..." : "Criar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
